/**
 * Turns the delivery line's business-day range into dates.
 *
 * The server writes "Delivered in 2–7 business days" and the dispatch rule,
 * because a cached page cannot know what day it is read. Here the element
 * works out, in the store's time zone, whether an order placed now still
 * ships today, and which days the parcel should arrive between.
 *
 * Business days are Monday to Friday. Public holidays are not known here, so
 * the ranges in the theme settings should allow for them.
 *
 * Data attributes (all written by snippets/snusify-delivery.liquid):
 * - data-min, data-max: business days in transit, after dispatch
 * - data-cutoff: "HH:MM" in the store's time zone
 * - data-offset: the store's UTC offset when the page was rendered, "+0200"
 * - data-zone: the store's time zone abbreviation, for the text
 * - data-city: where parcels ship from
 * - data-today, data-later, data-arrives: translated templates
 */
class SnusifyDelivery extends HTMLElement {
  connectedCallback() {
    const offset = SnusifyDelivery.#parseOffset(this.dataset.offset);
    const cutoff = SnusifyDelivery.#parseTime(this.dataset.cutoff);
    if (offset === null || cutoff === null) return;

    // The store's wall-clock time, carried in a UTC date so the calendar
    // maths below never meets the visitor's own time zone.
    const now = new Date(Date.now() + offset * 60000);
    const minutes = now.getUTCHours() * 60 + now.getUTCMinutes();
    const shipsToday = SnusifyDelivery.#isBusinessDay(now) && minutes < cutoff;
    const dispatch = shipsToday ? SnusifyDelivery.#startOfDay(now) : SnusifyDelivery.#addBusinessDays(now, 1);

    const lang = document.documentElement.lang || undefined;
    const dispatchLine = this.querySelector('[data-delivery-dispatch]');
    if (dispatchLine) {
      const city = this.dataset.city ?? '';
      dispatchLine.textContent = shipsToday
        ? SnusifyDelivery.#fill(this.dataset.today, { city, time: this.dataset.cutoff, zone: this.dataset.zone ?? '' })
        : SnusifyDelivery.#fill(this.dataset.later, {
            city,
            day: new Intl.DateTimeFormat(lang, { weekday: 'long', timeZone: 'UTC' }).format(dispatch),
          });
    }

    const min = Number.parseInt(this.dataset.min ?? '', 10);
    const max = Number.parseInt(this.dataset.max ?? '', 10);
    const arrives = this.querySelector('[data-delivery-arrives]');
    if (!arrives || Number.isNaN(min) || Number.isNaN(max)) return;

    const format = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    arrives.textContent = SnusifyDelivery.#fill(this.dataset.arrives, {
      from: format.format(SnusifyDelivery.#addBusinessDays(dispatch, min)),
      to: format.format(SnusifyDelivery.#addBusinessDays(dispatch, Math.max(min, max))),
    });
  }

  /**
   * @param {string | undefined} template - text with [name] placeholders
   * @param {Record<string, string>} values
   * @returns {string}
   */
  static #fill(template, values) {
    let text = template ?? '';
    for (const [name, value] of Object.entries(values)) text = text.replaceAll(`[${name}]`, value);
    return text;
  }

  /**
   * "+0200" or "-0530" in minutes; null when missing or malformed.
   * @param {string | undefined} value
   * @returns {number | null}
   */
  static #parseOffset(value) {
    const match = /^([+-])(\d{2})(\d{2})$/.exec(value ?? '');
    if (!match) return null;
    const minutes = Number(match[2]) * 60 + Number(match[3]);
    return match[1] === '-' ? -minutes : minutes;
  }

  /**
   * "11:00" in minutes after midnight; null when missing or malformed.
   * @param {string | undefined} value
   * @returns {number | null}
   */
  static #parseTime(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec((value ?? '').trim());
    if (!match) return null;
    return Number(match[1]) * 60 + Number(match[2]);
  }

  /** @param {Date} date */
  static #isBusinessDay(date) {
    const day = date.getUTCDay();
    return day !== 0 && day !== 6;
  }

  /** @param {Date} date */
  static #startOfDay(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  }

  /**
   * The date `count` business days after `date`, skipping weekends.
   * @param {Date} date
   * @param {number} count
   */
  static #addBusinessDays(date, count) {
    const result = SnusifyDelivery.#startOfDay(date);
    let left = count;
    while (left > 0) {
      result.setUTCDate(result.getUTCDate() + 1);
      if (SnusifyDelivery.#isBusinessDay(result)) left -= 1;
    }
    return result;
  }
}

if (!customElements.get('snusify-delivery')) {
  customElements.define('snusify-delivery', SnusifyDelivery);
}
